var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useState, useEffect } from "react";
import isEqual from "lodash/isEqual";
import { useHistory, useLocation } from "react-router-dom";
import { DEFAULT_PUBLISHER_STATUSES } from "../utils/filterDefaults";
import { appendSelectionParams, sameSelection, selectionFromParams, toggleRequiredSelection, } from "../utils/requiredSelection";
/**
 * The applied filters as the URL holds them.
 *
 * @param search `location.search` of the list
 */
var filtersFromSearch = function (search) {
    var queryParams = new URLSearchParams(search);
    return {
        name: queryParams.get("name") || "",
        // No statuses param in the URL falls back to the default view (active publishers).
        statuses: selectionFromParams(queryParams, "statuses", DEFAULT_PUBLISHER_STATUSES),
    };
};
export var usePublisherFilters = function () {
    var history = useHistory();
    var location = useLocation();
    var initialFilters = {
        name: "",
        statuses: DEFAULT_PUBLISHER_STATUSES,
    };
    // Initialised from the URL in the first render: filters patched in by an
    // effect afterwards would change the search and send it twice.
    var _a = useState(function () {
        return filtersFromSearch(location.search);
    }), filters = _a[0], setFilters = _a[1];
    var _b = useState(filters), appliedFilters = _b[0], setAppliedFilters = _b[1];
    var updateUrlWithFilters = function (newFilters) {
        var params = new URLSearchParams();
        if (newFilters.name)
            params.set("name", newFilters.name);
        appendSelectionParams(params, "statuses", newFilters.statuses, DEFAULT_PUBLISHER_STATUSES);
        history.push({ search: params.toString() });
    };
    var handleFilterChange = function (field, value) {
        var _a;
        var newFilters = __assign(__assign({}, filters), (_a = {}, _a[field] = value, _a));
        setFilters(newFilters);
    };
    var handleStatusChange = function (status) {
        var newStatuses = toggleRequiredSelection(filters.statuses, status);
        if (newStatuses === filters.statuses)
            return;
        handleFilterChange("statuses", newStatuses);
    };
    var handleApplyFilters = function () {
        setAppliedFilters(filters);
        updateUrlWithFilters(filters);
    };
    var handleClearFilters = function () {
        var clearedFilters = initialFilters;
        setFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        history.push({ search: "" });
    };
    var isAnyFilterActive = function () {
        return (appliedFilters.name.length > 0 ||
            !sameSelection(appliedFilters.statuses, DEFAULT_PUBLISHER_STATUSES));
    };
    var hasUnappliedChanges = function () {
        return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
    };
    var buildQuery = function (filterValues) {
        if (filterValues === void 0) { filterValues = appliedFilters; }
        var queryParts = [];
        if (filterValues.name.trim()) {
            var nameTerm = filterValues.name.trim();
            queryParts.push("(name==\"*".concat(nameTerm, "*\" or nameSuffix==\"*").concat(nameTerm, "*\" or qualifier==\"*").concat(nameTerm, "*\")"));
        }
        if (filterValues.statuses.length > 0) {
            var statusQueries = filterValues.statuses
                .map(function (status) { return "status==\"".concat(status, "\""); })
                .join(" or ");
            queryParts.push("(".concat(statusQueries, ")"));
        }
        return queryParts.length > 0 ? queryParts.join(" and ") : "";
    };
    useEffect(function () {
        var newFilters = filtersFromSearch(location.search);
        if (!isEqual(newFilters, appliedFilters)) {
            setFilters(newFilters);
            setAppliedFilters(newFilters);
        }
    }, [location.search, appliedFilters]);
    return {
        filters: filters,
        appliedFilters: appliedFilters,
        handleFilterChange: handleFilterChange,
        handleStatusChange: handleStatusChange,
        handleApplyFilters: handleApplyFilters,
        handleClearFilters: handleClearFilters,
        isAnyFilterActive: isAnyFilterActive,
        hasUnappliedChanges: hasUnappliedChanges,
        buildQuery: buildQuery,
    };
};
